// Variant Selector Custom Element
class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this.variantData = null;
    this.initializeVariantData();
    this.setupEventListeners();
  }

  initializeVariantData() {
    // Look for JSON data within the variant-selector element
    const jsonScript = this.querySelector('script[type="application/json"]');

    if (jsonScript) {
      try {
        const jsonData = JSON.parse(jsonScript.textContent);
        this.variantData = jsonData.variants || [];
        console.log('Variant data loaded:', this.variantData.length, 'variants found');
      } catch (error) {
        console.error('Error parsing variant JSON data:', error);
        this.variantData = [];
      }
    } else {
      console.warn('No variant JSON data found in variant-selector');
      this.variantData = [];
    }
  }

  setupEventListeners() {
    // Listen for change events on radio buttons within this variant selector
    this.addEventListener('change', this.handleVariantChange.bind(this));

    // Initialize checked state for radio buttons (fallback for :has() support)
    this.updateCheckedClasses();

    // Initialize the form with the currently selected variant
    this.initializeFormWithSelectedVariant();
  }

  initializeFormWithSelectedVariant() {
    // Find the currently checked radio button
    const checkedRadio = this.querySelector('input[type="radio"][name="id"]:checked');
    if (checkedRadio && checkedRadio.value) {
      console.log('Initializing form with checked variant:', checkedRadio.value);
      this.updateProductForm(checkedRadio.value);
    }
  }

  handleVariantChange(event) {
    // Handle radio button changes with name="id" or name="sticky_id"
    if (event.target.type === 'radio' && (event.target.name === 'id' || event.target.name === 'sticky_id')) {
      // Get variant ID from value attribute (most reliable)
      let selectedVariantId = event.target.value;

      // If no value, try to extract from id
      if (!selectedVariantId) {
        selectedVariantId = event.target.id.replace(/^(variant_|drawer_variant_)/, '');
      }

      const variantTitle = this.getVariantTitle(event.target);
      const variantDetails = this.getVariantById(selectedVariantId);

      console.log('Variant changed:', selectedVariantId, 'Title:', variantTitle, 'Details:', variantDetails);

      // Update the product form
      this.updateProductForm(selectedVariantId);

      // Dispatch custom event for other components
      this.dispatchVariantChangedEvent(selectedVariantId, variantTitle, event.target, variantDetails);

      // Update checked classes for visual feedback
      this.updateCheckedClasses();
    }
  }

  /**
   * Get variant details by variant ID
   * @param {number|string} variantId - The variant ID to look up
   * @returns {object|null} - The variant object if found, null otherwise
   */
  getVariantById(variantId) {
    if (!this.variantData || this.variantData.length === 0) {
      console.warn('No variant data available');
      return null;
    }

    const id = parseInt(variantId, 10);
    const variant = this.variantData.find(v => v.id === id);

    if (!variant) {
      console.warn('Variant with ID', variantId, 'not found in variant data');
      return null;
    }

    return variant;
  }

  /**
   * Get multiple variants by IDs
   * @param {array} variantIds - Array of variant IDs
   * @returns {array} - Array of variant objects
   */
  getVariantsByIds(variantIds) {
    if (!Array.isArray(variantIds)) {
      console.error('variantIds must be an array');
      return [];
    }

    return variantIds
      .map(id => this.getVariantById(id))
      .filter(variant => variant !== null);
  }

  /**
   * Get all variants
   * @returns {array} - Array of all variant objects
   */
  getAllVariants() {
    return this.variantData || [];
  }

  getVariantTitle(radioInput) {
    const label = radioInput.closest('.option_single_selector');
    const span = label ? label.querySelector('span') : null;
    return span ? span.textContent.trim() : '';
  }

  updateProductForm(variantId) {
    console.log('updateProductForm called with variantId:', variantId);

    // Find the product form using multiple strategies
    let form = null;

    // Strategy 1: Find product-form in the closest section
    const section = this.closest('.product-details-info');
    console.log('Closest section found:', !!section);
    if (section) {
      const productFormElement = section.querySelector('product-form form');
      if (productFormElement) {
        form = productFormElement;
        console.log('Strategy 1: Found form in section');
      }
    }

    if (form) {
      const hiddenInput = form.querySelector('input[name="id"]');

      if (hiddenInput) {
        console.log('✓ Found form:', form.id || form.className, 'Input current value:', hiddenInput.value, 'Setting to:', variantId);
        hiddenInput.value = variantId;
        console.log('✓ After update, input value is now:', hiddenInput.value);

        // Find the product-form custom element and trigger max quantity check
        const productFormElement = form.closest('product-form');
        if (productFormElement && typeof productFormElement.checkMaxQuantity === 'function') {
          console.log('✓ Calling checkMaxQuantity on product-form element');
          // Await the async checkMaxQuantity call to ensure button state updates
          productFormElement.checkMaxQuantity().then(() => {
            console.log('✓ checkMaxQuantity completed');
          }).catch(error => {
            console.error('Error in checkMaxQuantity:', error);
          });
        }

        // Verify it was set
        setTimeout(() => {
          console.log('✓ Verify (after 100ms): input value is:', hiddenInput.value);
        }, 100);
      } else {
        console.error('✗ Hidden input with name="id" not found in product form');
        console.log('Form HTML:', form.outerHTML.substring(0, 200));
      }
    } else {
      console.error('✗ Product form not found using any strategy');
      console.log('Looking for inputs with class product-variant-id:');
      console.log(document.querySelectorAll('input[name="id"].product-variant-id'));
    }
  }

  /**
   * Dispatch variant changed event with full variant details
   * @param {number|string} variantId - The variant ID
   * @param {string} variantTitle - The variant title/label
   * @param {HTMLElement} selectedInput - The selected radio input element
   * @param {object} variantDetails - The full variant object from JSON data
   */
  dispatchVariantChangedEvent(variantId, variantTitle, selectedInput, variantDetails = null) {
    // Dispatch event that other scripts can listen to
    const event = new CustomEvent('variant:changed', {
      bubbles: true,
      detail: {
        variantId: variantId,
        variantTitle: variantTitle,
        selectedInput: selectedInput,
        variant: variantDetails,
        variantSelector: this
      }
    });

    this.dispatchEvent(event);
    document.dispatchEvent(event); // Also dispatch globally
  }
  
  updateCheckedClasses() {
    // Add/remove 'checked' class on labels for visual feedback (fallback for :has() support)
    const radioButtons = this.querySelectorAll('input[type="radio"][name="id"], input[type="radio"][name="sticky_id"]');
    const labels = this.querySelectorAll('.option_single_selector');
    
    // Remove checked class from all labels
    labels.forEach(label => label.classList.remove('checked'));
    
    // Add checked class to labels with checked radio buttons
    radioButtons.forEach(radio => {
      if (radio.checked) {
        const label = radio.closest('.option_single_selector');
        if (label) {
          label.classList.add('checked');
          console.log('Added checked class to label for variant:', radio.value);
        }
      }
    });
  }
}

// Define the custom element
if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', VariantSelector);
}